import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FormViewService {

  constructor(private http: HttpClient) {}

  loadHierarchy(
    pdfEngineUrl: string,
    leType: string,
    entityType: string,
    taxYear: string,
    scenario: string,
    jcdKey: number,
    isState: boolean
  ): Observable<any> {
    const apiMethod = isState ? 'getStateGroupView' : 'getGtwView';
    let params = new HttpParams()
      .set('leType', leType)
      .set('entityType', entityType || '')
      .set('jcdKey', String(jcdKey))
      .set('scenario', scenario)
      .set('taxYear', taxYear)
      .set('src_system_flag', 'D')
      .set('formType', 'GTW');

    return this.http.get<any>(`${pdfEngineUrl}/${apiMethod}`, { params, withCredentials: true });
  }

  getTaxForms(
    pdfEngineUrl: string,
    selectedView: string,
    rptPeriod: string,
    leType: string,
    cdrNo: string,
    srcSystemFlag: string,
    businessType: string,
    entityType: string,
    selectedConsolGroup: string,
    taxYear: string,
    scenario: string,
    entityName: string,
    path: string,
    contSrc: string,
    fillingGroup: string,
    formType: string,
    keyId: any,
    submissionId: any,
    returnType: string,
    consolType: string,
    isAutoReload: string,
    jcdKey: number
  ): Observable<any> {
    let params = new HttpParams()
      .set('leType', leType || 'HO')
      .set('jcdKey', String(jcdKey))
      .set('scenario', scenario)
      .set('taxYears', taxYear)
      .set('src_system_flag', srcSystemFlag || 'D')
      .set('selectedView', selectedView)
      .set('cdrNo', cdrNo || '')
      .set('reportingPeriod', rptPeriod || '')
      .set('business_type', businessType)
      .set('entity_type', entityType || '')
      .set('entityName', entityName || '')
      .set('path', path || '')
      .set('contSrc', contSrc)
      .set('filing_group', fillingGroup || '')
      .set('formType', formType || '')
      .set('cgkey', selectedConsolGroup || '')
      .set('key_id', String(keyId || ''))
      .set(
        'submission_id',
        submissionId === null || submissionId === undefined || String(submissionId).trim() === ''
          ? 'null'
          : String(submissionId)
      )
      .set('return_type', returnType || '')
      .set('consol_type', consolType || '')
      .set('isAutoReload', isAutoReload || 'N');

    return this.http.post<any>(`${pdfEngineUrl}/getPdfTaxForms`, null, { params, withCredentials: true });
  }

  getPdf(pdfEngineUrl: string, pdfParams: any): Observable<ArrayBuffer> {
    // Use `??` so legitimate `0` (e.g. `keyId`, `is_attachment`, `is_statement`,
    // `cgKey`) is preserved rather than coerced to `''`. `HttpParams` URL-encodes
    // values once internally — legacy `requestBuilderFactory.appendParam` does
    // NOT pre-encode either, so we must not call `encodeURIComponent(...)` here
    // (it caused `&` in `leName` to become `%2526` after Angular's encoding).
    const str = (v: any): string => v === null || v === undefined ? '' : String(v);

    let params = new HttpParams()
      .set('irsFormNo', str(pdfParams.formName))
      .set('wpIrsFormNo', str(pdfParams.wpIrsFormNo))
      .set('s3Url', str(pdfParams.s3_url))
      .set('formSubCat', str(pdfParams.formSubCat))
      .set('formSub', str(pdfParams.formSub))
      .set('leType', str(pdfParams.leType))
      .set('jcdKey', str(pdfParams.jcdKey))
      .set('scenario', str(pdfParams.scenarioLocal))
      .set('taxYear', str(pdfParams.taxYear))
      .set('systemFlag', str(pdfParams.sourceSystemFlag ?? 'D'))
      .set('leid', str(pdfParams.leId))
      .set('parentLeId', str(pdfParams.parentLeId))
      .set('cdrNo', str(pdfParams.cdrNo))
      .set('rptPeriod', str(pdfParams.rptPeriod))
      .set('schedule', str(pdfParams.schedule))
      .set('line', str(pdfParams.line))
      .set('partNo', str(pdfParams.partNo))
      .set('entity_type', str(pdfParams.selectedEntityType))
      .set('leName', str(pdfParams.leName))
      .set('contSrc', str(pdfParams.contSrc))
      .set('ein', str(pdfParams.ein))
      .set('consolType', str(pdfParams.consolType))
      .set('cgKey', str(pdfParams.selectedConsolGroup))
      .set('keyId', str(pdfParams.keyId))
      .set('submissionId', str(pdfParams.submission_id))
      .set('returnType', str(pdfParams.returnType))
      .set('batchId', str(pdfParams.batchId))
      .set('srcType', str(pdfParams.srcType))
      .set('jobId', '0')
      .set('mergeAll', str(pdfParams.mergeAll ?? false))
      .set('withDrillDowns', str(pdfParams.withDrillDowns ?? '1'))
      .set('is_attachment', str(pdfParams.is_attachment ?? 0))
      .set('is_statement', str(pdfParams.is_statement ?? 0));

    return this.http.get(
      `${pdfEngineUrl}/fillForm`,
      { params, responseType: 'arraybuffer', withCredentials: true }
    );
  }

  mergePdf(pdfEngineUrl: string, pdfParams: any): Observable<ArrayBuffer> {
    const body = {
      jobId: 0,
      irsParentFormList: pdfParams.wpIrsFormNo,
      irsFormList: pdfParams.formName,
      formSubList: pdfParams.formSub,
      attachmentValues: pdfParams.is_attachment,
      statementValues: pdfParams.is_statement,
      entity: pdfParams.leId,
      taxYear: pdfParams.taxYear,
      cdrNo: pdfParams.cdrNo,
      rptPeriod: pdfParams.rptPeriod,
      leType: pdfParams.leType,
      systemFlag: pdfParams.sourceSystemFlag,
      scheduleList: pdfParams.schedule,
      lineList: pdfParams.line,
      partNoList: pdfParams.partNo,
      jcdKey: pdfParams.jcdKey,
      scenario: pdfParams.scenarioLocal,
      leName: pdfParams.leName,
      einList: pdfParams.ein,
      srcType: pdfParams.srcType,
      entity_type: pdfParams.selectedEntityType,
      cgkey: pdfParams.selectedConsolGroup,
      parentLeId: pdfParams.parentLeId,
      consolType: pdfParams.consolType,
      keyId: pdfParams.keyId,
      submissionId: pdfParams.submission_id,
      returnType: pdfParams.returnType,
      batchId: pdfParams.batchId,
      mergeAll: pdfParams.mergeAll
    };

    return this.http.post(
      `${pdfEngineUrl}/mergeProforma`,
      body,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        responseType: 'arraybuffer',
        withCredentials: true
      }
    );
  }

  pushPdfsToS3(pdfEngineUrl: string, taxYear: string, scenario: string, jcdKey: number): Observable<any> {
    let params = new HttpParams()
      .set('leType', 'FORM')
      .set('jcdKey', String(jcdKey))
      .set('scenario', scenario)
      .set('taxYear', taxYear)
      .set('src_system_flag', 'D')
      .set('formType', 'GTW')
      .set('returnType', '');

    return this.http.get<any>(`${pdfEngineUrl}/getGtwS3View`, { params, withCredentials: true });
  }

  updateS3Status(
    pdfEngineUrl: string,
    taxYear: string,
    leid: string,
    leType: string,
    keyId: any,
    submissionId: any
  ): Observable<any> {
    let params = new HttpParams()
      .set('taxYear', taxYear)
      .set('leid', leid)
      .set('leType', leType)
      .set('key_id', String(keyId))
      .set('submission_id', String(submissionId));

    return this.http.post<any>(`${pdfEngineUrl}/updateEntityS3UploadStatus`, null, { params, withCredentials: true });
  }

  getCombinationKeysByEntity(
    baseUrl: string,
    ssoId: string,
    taxYear: string,
    scenario: string,
    jcdKey: number,
    leId: string,
    cdrNo: string,
    rptPeriod: string,
    leType: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('sso_id', ssoId)
      .set('tax_year', taxYear)
      .set('scenario', scenario)
      .set('jcd_key', String(jcdKey))
      .set('action_id', '32036')
      .set('leid', leId)
      .set('cdrNo', cdrNo || '')
      .set('rptPeriod', rptPeriod || '')
      .set('le_type', leType);

    return this.http.post<any>(`${baseUrl}/loadJsonObject`, null, { params, withCredentials: true });
  }

  loadIRSId(baseUrl: string, submissionId: any): Observable<any> {
    let params = new HttpParams()
      .set('action_code', 'u0pmel')
      .set('submission_id', String(submissionId));

    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getDomProcessList(pdfEngineUrl: string): Observable<any> {
    return this.http.get<any>(`${pdfEngineUrl}/getDomProcessingList`, { withCredentials: true });
  }

  downloadXMLorZIP(bulkPdfUrl: string, submissionId: any, keyId: any, leType: string, fileType: string): Observable<ArrayBuffer> {
    let params = new HttpParams()
      .set('submissionId', String(submissionId))
      .set('keyId', String(keyId))
      .set('leType', leType)
      .set('type', fileType);

    return this.http.get(
      `${bulkPdfUrl}/downloadXmlORZip`,
      { params, responseType: 'arraybuffer', withCredentials: true }
    );
  }

  createSnapshot(bulkPdfUrl: string, submissionId: any, taxYear: string, scenario: string, jcdKey: number, ignoreDiag: boolean): Observable<any> {
    let params = new HttpParams()
      .set('taxYear', taxYear)
      .set('scenario', scenario)
      .set('jcdKey', String(jcdKey))
      .set('submissionId', String(submissionId))
      .set('ignoreDiag', ignoreDiag ? 'Y' : 'N');

    return this.http.get<any>(`${bulkPdfUrl}/createSnapshot`, { params, withCredentials: true });
  }

  getBulkExportForms(pdfEngineUrl: string, taxYear: string, returnType: string): Observable<any> {
    let params = new HttpParams()
      .set('taxYear', taxYear)
      .set('submissionId', '')
      .set('returnType', returnType || '')
      .set('srcType', '')
      .set('parentLeid', '')
      .set('formType', '');

    return this.http.post<any>(`${pdfEngineUrl}/getBulkExportForms`, null, { params, withCredentials: true });
  }

  pdfsBulkExport(
    bulkPdfUrl: string,
    taxYear: string,
    scenario: string,
    jcdKey: number,
    format: string,
    mode: string,
    contSrc: string,
    body: any
  ): Observable<any> {
    let params = new HttpParams()
      .set('taxYear', taxYear)
      .set('scenario', scenario)
      .set('jcdKey', String(jcdKey))
      .set('mode', mode)
      .set('format', format)
      .set('contSrc', contSrc);

    return this.http.post<any>(
      `${bulkPdfUrl}/pdfS3BulkExport`,
      body,
      {
        params,
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        withCredentials: true
      }
    );
  }

  /**
   * Bulk-export selection list (legacy `formViewServices.js` L294–296):
   * `POST ${GENERAL_CONFIG.base_url}/loadJsonObject?action_id=31506&sso_id=…&p_request_id=…&get_ents_frms=…`
   * with no body. Query is embedded in the URL string (same as AngularJS $http.post).
   */
  getRequestDetails(baseUrl: string, ssoId: string, requestId: string, getEntsFrms: string): Observable<any> {
    const root = (baseUrl || '').replace(/\/+$/, '');
    const q = new URLSearchParams({
      action_id: '31506',
      sso_id: String(ssoId),
      p_request_id: String(requestId),
      get_ents_frms: String(getEntsFrms),
    });
    const url = `${root}/loadJsonObject?${q.toString()}`;
    return this.http.post<any>(url, null, { withCredentials: true });
  }

  getFormTemplate(bulkPdfUrl: string, rowData: any): Observable<ArrayBuffer> {
    // Only append params that have actual values. AngularJS `$http.params`
    // strips undefined values automatically, so the legacy URL is
    //   /getFormForS3Template?irsFormNo=…&s3UrlSLTEntry=…&taxYear=…
    // (no trailing `&isDownloadRequest=` when not downloading). Sending an
    // empty `isDownloadRequest=` was breaking the bulk-pdf-engine response.
    // HttpParams serializes in append order — keep irsFormNo → s3UrlSLTEntry → taxYear
    // to match bulk-pdf-engine expectations.
    let params = new HttpParams();
    if (rowData?.IRS_FORM_NO != null && rowData.IRS_FORM_NO !== '') {
      params = params.set('irsFormNo', String(rowData.IRS_FORM_NO));
    }
    if (rowData?.FILE_KEY != null && rowData.FILE_KEY !== '') {
      params = params.set('s3UrlSLTEntry', String(rowData.FILE_KEY));
    }
    if (rowData?.TAX_YEAR != null && rowData.TAX_YEAR !== '') {
      params = params.set('taxYear', String(rowData.TAX_YEAR));
    }
    if (rowData?.isDownloadRequest) {
      params = params.set('isDownloadRequest', String(rowData.isDownloadRequest));
    }

    return this.http.get(
      `${bulkPdfUrl}/getFormForS3Template`,
      { params, responseType: 'arraybuffer', withCredentials: true }
    );
  }

  uploadPdf(bulkPdfUrl: string, taxYear: string, irsFormNo: string, fileKey: string, comment: string, file: File): Observable<any> {
    const fd = new FormData();
    fd.append('file', file);

    let params = new HttpParams()
      .set('tax_year', taxYear)
      .set('irs_form_no', irsFormNo)
      .set('fileKey', fileKey || '')
      .set('comment', comment || '');

    return this.http.post<any>(`${bulkPdfUrl}/uploadTemplate`, fd, { params, withCredentials: true });
  }

  exportMappingsToExcel(bulkPdfUrl: string, taxYear: string, irsFormNo: string): Observable<ArrayBuffer> {
    let params = new HttpParams()
      .set('taxYear', taxYear)
      .set('irsFormNo', irsFormNo);

    return this.http.get(
      `${bulkPdfUrl}/ddToExcelExport`,
      { params, responseType: 'arraybuffer', withCredentials: true }
    );
  }
}
